import React, { useState, useEffect } from "react";

function SearchTab({ 
  onShowOnMap, 
  onCompleteSelection,
  selectedSido,
  setSelectedSido,
  selectedSigungu,
  setSelectedSigungu,
  cityCode,
  setCityCode
}) {
  const [sidoList, setSidoList] = useState([]); // [{name, code}]
  const [sigunguList, setSigunguList] = useState([]); // [{name, code}]
  const [routeNo, setRouteNo] = useState("");
  const [routeId, setRouteId] = useState(null);
  const [routeError, setRouteError] = useState(null);
  const [routeList, setRouteList] = useState([]); // 여러 노선 후보
  const [selectedRoute, setSelectedRoute] = useState(null); // 사용자가 선택한 노선
  const [busStops, setBusStops] = useState([]);
  const [busStopsLoading, setBusStopsLoading] = useState(false);
  const [busStopsError, setBusStopsError] = useState(null);
  const [selectedStops, setSelectedStops] = useState([]); // 선택된 정류장들
  const [lastRouteInfo, setLastRouteInfo] = useState(null); // {routeId, cityCode, endnodenm}
  const [routeInterval, setRouteInterval] = useState(null); // 배차간격 정보
  const [stopSearch, setStopSearch] = useState(""); // 정류장 검색어 상태 추가


  
  // sido.json에서 시/도, 시/군/구 목록 불러오기
  useEffect(() => {
    fetch(process.env.PUBLIC_URL + "/sido.json")
      .then(res => res.json())
      .then(json => {
        const items = json.response.body.items.item;
        // 시/도만 추출 (코드가 2자리 또는 39(제주))
        const sidoArr = items.filter(i => String(i.citycode).length === 2 || i.citycode === 39)
          .map(i => ({ name: i.cityname.replace("도", "도").replace("광역시/계룡시", "광역시"), code: i.citycode }));
        setSidoList(sidoArr);
      });
  }, []);

  // 시/도 선택 시 시/군/구 목록 추출
  useEffect(() => {
    if (!selectedSido) {
      setSigunguList([]);
      setCityCode(null);
      return;
    }
    fetch(process.env.PUBLIC_URL + "/sido.json")
      .then(res => res.json())
      .then(json => {
        const items = json.response.body.items.item;
        // 선택한 시/도 코드
        const sidoCode = selectedSido;
        // 시/군/구: 코드가 5자리, 시/도 코드로 시작
        const sigunguArr = items.filter(i => String(i.citycode).length === 5 && String(i.citycode).startsWith(String(sidoCode)))
          .map(i => ({ name: i.cityname, code: i.citycode }));
        setSigunguList(sigunguArr);
        // 하위 시/군/구 없으면 바로 cityCode 지정
        if (sigunguArr.length === 0) setCityCode(sidoCode);
        else setCityCode(null);
      });
  }, [selectedSido, setCityCode]);

  // 시/군/구 선택 시 cityCode 지정
  useEffect(() => {
    if (selectedSigungu) setCityCode(selectedSigungu);
    else if (sigunguList.length === 0 && selectedSido) setCityCode(selectedSido);
    else setCityCode(null);
  }, [selectedSigungu, sigunguList, selectedSido, setCityCode]);

  // 노선번호로 routeId 조회
  const handleRouteSearch = async () => {
    setRouteId(null);
    setRouteError(null);
    setRouteList([]);
    setSelectedRoute(null);
    setBusStops([]);
    setBusStopsError(null);
    setSelectedStops([]);
    setLastRouteInfo(null);
    setStopSearch("");
    if (!cityCode || !routeNo) {
      setRouteError("도시코드와 노선번호를 모두 입력하세요.");
      return;
    }

    // 시/도 코드가 서울(11)일 때는 서울시 API 사용
    if (String(cityCode).startsWith("11")) {
      try {
        const serviceKey = "KkkVy4H1CGa8fSuj2QR5%2BSHvd1oyW2RO%2FmtyS2Sr6ExzX34N5NoaduPT%2BpIuyWLRcQcIkJGT3OI%2Bu3Mv7mH9qA%3D%3D";
        // 1. 노선번호로 노선 ID 조회
        const url = `/busapi/api/rest/busRouteInfo/getBusRouteList?serviceKey=${serviceKey}&strSrch=${routeNo}`;
        const res = await fetch(url);
        const xmlText = await res.text();
        const parser = new window.DOMParser();
        const xml = parser.parseFromString(xmlText, "text/xml");
        const items = xml.querySelectorAll("itemList");
        if (items.length === 0) {
          setRouteError("해당 노선이 없습니다.");
          return;
        }
        // 여러 노선 후보가 있을 수 있음
        const list = Array.from(items).map(item => {
          const busRouteId = item.querySelector("busRouteId")?.textContent;
          const busRouteNm = item.querySelector("busRouteNm")?.textContent;
          const stStationNm = item.querySelector("stStationNm")?.textContent;
          const edStationNm = item.querySelector("edStationNm")?.textContent;
          const routeType = item.querySelector("routeType")?.textContent;
          return {
            routeId: busRouteId,
            routeno: busRouteNm,
            startnodenm: stStationNm,
            endnodenm: edStationNm,
            cityCode,
            sigunguName: "서울",
            routeType // 추가
          };
        });
        if (list.length === 1) {
          setSelectedRoute(list[0]);
          setRouteId(list[0].routeId);
          setLastRouteInfo({ routeId: list[0].routeId, cityCode, endnodenm: list[0].endnodenm });
          // 배차간격 정보 가져오기
          await fetchRouteInterval(list[0].routeId, cityCode);
        } else {
          setRouteList(list);
        }
        // 2. 정류소 목록 조회 (선택된 노선이 있을 때만)
        let targetRoute = list[0];
        if (list.length > 1) {
          // 여러 후보 중 첫 번째를 임시로 사용(실제 선택 시 다시 조회)
          targetRoute = list[0];
        }
        if (targetRoute) {
          const stopUrl = `/busapi/api/rest/busRouteInfo/getStaionByRoute?serviceKey=${serviceKey}&busRouteId=${targetRoute.routeId}`;
          const stopRes = await fetch(stopUrl);
          const stopXml = await stopRes.text();
          const stopParser = new window.DOMParser();
          const stopDoc = stopParser.parseFromString(stopXml, "text/xml");
          const stopItems = stopDoc.querySelectorAll("itemList");
          const stops = Array.from(stopItems).map(item => ({
            nodeid: item.querySelector("station")?.textContent,
            nodenm: item.querySelector("stationNm")?.textContent,
            gpslati: item.querySelector("gpsY")?.textContent,
            gpslong: item.querySelector("gpsX")?.textContent,
            nodeord: item.querySelector("seq")?.textContent,
            routeid: targetRoute.routeId
          }));
          setBusStops(stops);
        }
        return;
      } catch (e) {
        setRouteError(`서울시 API 조회 중 오류가 발생했습니다.\n${e?.message || e}`);
        return;
      }
    }

    // 1. 먼저 선택한 시/군/구(cityCode)로만 조회
    try {
      const serviceKey = "KkkVy4H1CGa8fSuj2QR5%2BSHvd1oyW2RO%2FmtyS2Sr6ExzX34N5NoaduPT%2BpIuyWLRcQcIkJGT3OI%2Bu3Mv7mH9qA%3D%3D";
      const url = `https://apis.data.go.kr/1613000/BusRouteInfoInqireService/getRouteNoList?serviceKey=${serviceKey}&pageNo=1&numOfRows=10&_type=xml&cityCode=${cityCode}&routeNo=${routeNo}`;
      const res = await fetch(url);
      const xmlText = await res.text();
      const parser = new window.DOMParser();
      const xml = parser.parseFromString(xmlText, "text/xml");
      const items = xml.querySelectorAll("item");
      if (items.length === 1) {
        const item = items[0];
        const routeId = item.querySelector("routeid")?.textContent;
        const startnodenm = item.querySelector("startnodenm")?.textContent;
        const endnodenm = item.querySelector("endnodenm")?.textContent;
        const routeno = item.querySelector("routeno")?.textContent;
        const routetp = item.querySelector("routetp")?.textContent;
        
        // 시/군/구 이름 찾기
        let sigunguName = null;
        if (sigunguList.length > 0) {
          const found = sigunguList.find(sg => sg.code === cityCode);
          sigunguName = found ? found.name : null;
        } else if (sidoList.length > 0) {
          const found = sidoList.find(sd => String(sd.code) === String(cityCode));
          sigunguName = found ? found.name : null;
        }
        setRouteId(`${routeId || "routeId 없음"}${sigunguName ? ` (${sigunguName})` : ""}${startnodenm || endnodenm ? ` [${startnodenm || "?"}~${endnodenm || "?"}]` : ""}`);
        setLastRouteInfo({ routeId, cityCode, endnodenm });
        setSelectedRoute({ routeId, routeno, startnodenm, endnodenm, cityCode, sigunguName, routeType: routetp });
        // 배차간격 정보 가져오기
        await fetchRouteInterval(routeId, cityCode);
        return;
      } else if (items.length > 1) {
        // 여러 노선 후보가 있을 때
        const list = Array.from(items).map(item => {
          const routeId = item.querySelector("routeid")?.textContent;
          const startnodenm = item.querySelector("startnodenm")?.textContent;
          const endnodenm = item.querySelector("endnodenm")?.textContent;
          const routeno = item.querySelector("routeno")?.textContent;
          const routetp = item.querySelector("routetp")?.textContent;
          return {
            routeId,
            startnodenm,
            endnodenm,
            routeno,
            cityCode,
            sigunguName: (() => {
              if (sigunguList.length > 0) {
                const found = sigunguList.find(sg => sg.code === cityCode);
                return found ? found.name : null;
              } else if (sidoList.length > 0) {
                const found = sidoList.find(sd => String(sd.code) === String(cityCode));
                return found ? found.name : null;
              }
              return null;
            })(),
            routeType: routetp
          };
        });
        setRouteList(list);
        return;
      }
    } catch (e) {
      // 무시하고 다음 단계로
    }

    // 2. 결과가 없을 때만 도내 모든 시/군/구를 순차적으로 검색
    let found = false;
    
    let prevRouteError = routeError;
    if (sigunguList.length > 0) {
      setRouteError('범위를 확대해 재검색중...');
    }
    if (sigunguList.length > 0) {
      for (let sg of sigunguList) {
        try {
          const serviceKey = "KkkVy4H1CGa8fSuj2QR5%2BSHvd1oyW2RO%2FmtyS2Sr6ExzX34N5NoaduPT%2BpIuyWLRcQcIkJGT3OI%2Bu3Mv7mH9qA%3D%3D";
          const url = `https://apis.data.go.kr/1613000/BusRouteInfoInqireService/getRouteNoList?serviceKey=${serviceKey}&pageNo=1&numOfRows=10&_type=xml&cityCode=${sg.code}&routeNo=${routeNo}`;
          const res = await fetch(url);
          const xmlText = await res.text();
          const parser = new window.DOMParser();
          const xml = parser.parseFromString(xmlText, "text/xml");
          const items = xml.querySelectorAll("item");
          if (items.length === 1) {
            const item = items[0];
            const routeId = item.querySelector("routeid")?.textContent;
            const startnodenm = item.querySelector("startnodenm")?.textContent;
            const endnodenm = item.querySelector("endnodenm")?.textContent;
            const routeno = item.querySelector("routeno")?.textContent;
            const routetp = item.querySelector("routetp")?.textContent;
            

            setRouteId(`${routeId || "routeId 없음"} (${sg.name})${startnodenm || endnodenm ? ` [${startnodenm || "?"}~${endnodenm || "?"}]` : ""}`);
            setLastRouteInfo({ routeId, cityCode: sg.code, endnodenm });
            setSelectedRoute({ routeId, routeno, startnodenm, endnodenm, cityCode: sg.code, sigunguName: sg.name, routeType: routetp });
            await fetchRouteInterval(routeId, sg.code);

            found = true;
           
            break;
          } else if (items.length > 1) {
            // 여러 노선 후보가 있을 때
            const list = Array.from(items).map(item => {
              const routeId = item.querySelector("routeid")?.textContent;
              const startnodenm = item.querySelector("startnodenm")?.textContent;
              const endnodenm = item.querySelector("endnodenm")?.textContent;
              const routeno = item.querySelector("routeno")?.textContent;
              const routetp = item.querySelector("routetp")?.textContent;
              return {
                routeId,
                startnodenm,
                endnodenm,
                routeno,
                cityCode: sg.code,
                sigunguName: sg.name,
                routeType: routetp
              };
            });
            setRouteList(list);
            found = true;
            break;
          }
        } catch (e) {
          // 무시하고 다음 코드로 진행
        }
      }
    }
    if (sigunguList.length > 0) {
      setRouteError(prevRouteError);
    }
    if (!found && routeList.length === 0) {
      setRouteError("해당 도 전체에서 노선 정보를 찾을 수 없습니다.");
    }
  };

  // 사용자가 노선 후보 중 하나를 선택했을 때
  const handleRouteSelect = async (route) => {
    setSelectedRoute(route);
    setRouteId(`${route.routeId || "routeId 없음"}${route.sigunguName ? ` (${route.sigunguName})` : ""}${route.startnodenm || route.endnodenm ? ` [${route.startnodenm || "?"}~${route.endnodenm || "?"}]` : ""}`);
    setLastRouteInfo({ routeId: route.routeId, cityCode: route.cityCode, endnodenm: route.endnodenm });

    // 배차간격 정보 가져오기
    await fetchRouteInterval(route.routeId, route.cityCode);

    // 서울시 노선이면 정류소 목록을 서울시 API로 조회
    if (route.sigunguName === "서울") {
      try {
        const serviceKey = "KkkVy4H1CGa8fSuj2QR5%2BSHvd1oyW2RO%2FmtyS2Sr6ExzX34N5NoaduPT%2BpIuyWLRcQcIkJGT3OI%2Bu3Mv7mH9qA%3D%3D";
        const stopUrl = `/busapi/api/rest/busRouteInfo/getStaionByRoute?serviceKey=${serviceKey}&busRouteId=${route.routeId}`;
        const stopRes = await fetch(stopUrl);
        const stopXml = await stopRes.text();
        const stopParser = new window.DOMParser();
        const stopDoc = stopParser.parseFromString(stopXml, "text/xml");
        const stopItems = stopDoc.querySelectorAll("itemList");
        const stops = Array.from(stopItems).map(item => ({
          nodeid: item.querySelector("station")?.textContent,
          nodenm: item.querySelector("stationNm")?.textContent,
          gpslati: item.querySelector("gpsY")?.textContent,
          gpslong: item.querySelector("gpsX")?.textContent,
          nodeord: item.querySelector("seq")?.textContent,
          routeid: route.routeId
        }));
        setBusStops(stops);

      } catch (e) {
        setBusStopsError("서울시 정류소 목록 조회 오류");
      }
    }
  };

  // 버스 정차 정류소 목록 불러오기
  useEffect(() => {
    if (!lastRouteInfo || !lastRouteInfo.routeId || !lastRouteInfo.cityCode) {
      setBusStops([]);
      return;
    }
    // 서울시 노선이면 국토부 API 호출하지 않음
    if (String(lastRouteInfo.cityCode).startsWith("11")) {
      return;
    }
    setBusStopsLoading(true);
    setBusStopsError(null);
    const serviceKey = "KkkVy4H1CGa8fSuj2QR5%2BSHvd1oyW2RO%2FmtyS2Sr6ExzX34N5NoaduPT%2BpIuyWLRcQcIkJGT3OI%2Bu3Mv7mH9qA%3D%3D";
    const url = `https://apis.data.go.kr/1613000/BusRouteInfoInqireService/getRouteAcctoThrghSttnList?serviceKey=${serviceKey}&pageNo=1&numOfRows=200&_type=json&cityCode=${lastRouteInfo.cityCode}&routeId=${lastRouteInfo.routeId}`;
    fetch(url)
      .then(res => res.json())
      .then(json => {
        let items = json?.response?.body?.items?.item;
        if (!items) setBusStops([]);
        else if (!Array.isArray(items)) setBusStops([items]);
        else setBusStops(items);
      })
      .catch(e => setBusStopsError("정류소 목록을 불러오지 못했습니다."))
      .finally(() => setBusStopsLoading(false));
  }, [lastRouteInfo]);

  // 정류장 클릭 시 선택/해제
  const handleStopClick = (stop) => {
    setSelectedStops(prev => {
      const isSelected = prev.some(s => s.nodeid === stop.nodeid);
      if (isSelected) {
        // 선택 해제
        return prev.filter(s => s.nodeid !== stop.nodeid);
      } else {
        // 선택 (최대 2개까지만)
        if (prev.length >= 2) {
          return prev.slice(1).concat(stop); // 첫 번째 제거하고 새로 추가
        } else {
          return [...prev, stop];
        }
      }
    });
    setStopSearch("");
  };

  // 정류장 선택 상태 확인
  const isStopSelected = (stop) => {
    return selectedStops.some(s => s.nodeid === stop.nodeid);
  };

  // 배차간격 정보 가져오기
  const fetchRouteInterval = async (routeId, cityCode) => {




    try {
      const serviceKey = "KkkVy4H1CGa8fSuj2QR5%2BSHvd1oyW2RO%2FmtyS2Sr6ExzX34N5NoaduPT%2BpIuyWLRcQcIkJGT3OI%2Bu3Mv7mH9qA%3D%3D";
      let intervalData = null;
      
      // 서울시 노선인 경우
      if (String(cityCode).startsWith("11")) {
        const url = `/busapi/api/rest/busRouteInfo/getRouteInfo?serviceKey=${serviceKey}&busRouteId=${routeId}`;
        const response = await fetch(url);
        const xmlText = await response.text();
        const parser = new window.DOMParser();
        const xml = parser.parseFromString(xmlText, "text/xml");
        const item = xml.querySelector("itemList");
        
        if (item) {
          intervalData = {
            weekday: item.querySelector("term")?.textContent || "-",
            saturday: item.querySelector("term")?.textContent || "-",
            holiday: item.querySelector("term")?.textContent || "-"
          };
        }
      } else {
        // 국토부 API 사용
        const url = `https://apis.data.go.kr/1613000/BusRouteInfoInqireService/getRouteInfoIem?serviceKey=${serviceKey}&pageNo=1&numOfRows=10&_type=json&cityCode=${cityCode}&routeId=${routeId}`;
        const response = await fetch(url);
        const data = await response.json();
        const items = data?.response?.body?.items?.item;
        
        if (items) {
          const item = Array.isArray(items) ? items[0] : items;
          intervalData = {
            weekday: item.intervaltime || "-",
            saturday: item.intervalsattime || "-", 
            holiday: item.intervalsuntime || "-"
          };
        }
      }
      
      setRouteInterval(intervalData);
      return intervalData;
    } catch (error) {
      console.error("배차간격 정보 조회 실패:", error);
      setRouteInterval(null);
      return null;
    }
  };

  // 정류장 선택 순서 확인 (승차지/하차지)
  const getStopType = (stop) => {
    const index = selectedStops.findIndex(s => s.nodeid === stop.nodeid);
    if (index === -1) return null;
    
    // 선택된 정류장이 2개일 때만 승차지/하차지 표시
    if (selectedStops.length === 2) {
      const [first, second] = selectedStops;
      // 순번(nodeord) 기준으로 승차지/하차지 결정
      if (stop.nodeord <= Math.min(first.nodeord, second.nodeord)) {
        return "승차지";
      } else {
        return "하차지";
      }
    }
    return null;
  };

  // 노선 및 정류장 정보가 모두 준비된 경우에만 버튼 활성화
  //const canShowOnMap = selectedRoute;

  // 지도에서 보기용 데이터 구조


  // routeType 변환 함수
  function formatRouteType(rt) {
    if (!rt) return '';
    // 숫자 코드 변환
    const typeMap = {
      '1': '공항',
      '2': '마을',
      '3': '간선',
      '4': '지선',
      '5': '순환',
      '6': '광역',
      '7':'인천',
      '8':'경기',
      '9':'폐지',
      '10':'관광',
      '0':'공용'

    };
    if (typeMap[rt]) return typeMap[rt];
    // 맨 뒤 '버스' 제거
    return rt.replace(/버스$/, '');
  }

  // 모바일/웹 접속에 따라 CORS 프록시 여부를 결정하는 함수 추가
  // function getSeoulApiUrl(path) {
  //   // 모바일 접속 여부 판단 (userAgent 기반)
  //   const isMobile = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  //   const base = isMobile
  //     ? 'http://ws.bus.go.kr/api/rest/busRouteInfo/'
  //     : 'http://ws.bus.go.kr/api/rest/busRouteInfo/'
  //   return base + path;
  // }

  return (
    <div style={{ padding: 24 }}>
      {/*<h2 style={{ color: "#3498db", marginBottom: 24 }}>지역 검색</h2>*/}
      <span style={{ marginBottom: 10 }}>
        <label style={{ fontWeight: "bold", marginRight: 10 }}>노선 검색</label>
        <select
          value={selectedSido}
          onChange={e => { setSelectedSido(e.target.value); setSelectedSigungu(""); }}
          style={{ fontSize: 16, padding: 6, width: 120 }}
        >
          <option value="">시/도 선택</option>
          {sidoList.map(sido => (
            <option key={sido.code} value={sido.code}>{sido.name}({sido.code})</option>
          ))}
        </select>
      </span>
      {sigunguList.length > 0 && (
        <span style={{ marginBottom: 10 }}>
          <select
            value={selectedSigungu}
            onChange={e => setSelectedSigungu(e.target.value)}
            style={{ fontSize: 16, padding: 6, width: 120 }}
          >
            <option value="">시/군 선택</option>
            {sigunguList.map(sg => (
              <option key={sg.code} value={sg.code}>{sg.name}({sg.code})</option>
            ))}
          </select>
        </span>
      )}


      {cityCode && (
        <div style={{ marginTop: 0, padding: 8, background: "#f8f9fa", borderRadius: 8 }}>
          {/*<h2 style={{ color: "#3498db", marginBottom: 24 }}>지역 검색</h2>*/}
          <div style={{ marginBottom: 10 }}>
            <label style={{ marginRight: 8 }}>노선번호 입력</label>
            <input
              type="text"
              value={routeNo}
              onChange={e => setRouteNo(e.target.value)}
              style={{ fontSize: 16, padding: 4, width: 100 }}
              placeholder="예: 8106"
            />
            <button
              onClick={handleRouteSearch}
              style={{
                marginLeft: 10,
                padding: "4px 6px",
                fontSize: 15,
                background: "#3498db",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                cursor: "pointer"
              }}
            >
              조회
            </button>
          </div>
          {routeList && routeList.length > 0 && !selectedRoute && (
            <div style={{ marginBottom: 10, background: "#f4f8fb", borderRadius: 8, padding: 12, border: "1px solid #d0e2f2" }}>
              <div style={{ fontWeight: "bold", color: "#3498db", marginBottom: 8 }}>노선이 여러 개 검색되었습니다. 선택하세요:</div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {routeList.map((route, idx) => (
                  <li key={route.routeId + idx} style={{ marginBottom: 6 }}>
                    <button
                      onClick={() => handleRouteSelect(route)}
                     
                      style={{
                        background: selectedRoute && selectedRoute.routeId === route.routeId ? "#3498db" : "#fff",
                        color: selectedRoute && selectedRoute.routeId === route.routeId ? "#fff" : "#222",
                        border: "1px solid #3498db",
                        borderRadius: 5,
                        padding: "6px 12px",
                        cursor: "pointer",
                        width: "100%",
                        textAlign: "left"
                      }}
                    >
                      {route.routeno} {route.sigunguName ? `(${route.sigunguName})` : ""} {route.startnodenm || route.endnodenm ? ` [${route.startnodenm || "?"}~${route.endnodenm || "?"}]` : ""}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {routeId && (
            <div style={{ color: "#222", fontWeight: "bold" }}>
              {/* 노선번호, 시군구, 기점~종점, routeId 부가정보 (노선번호를 메인으로) */}
              {selectedRoute ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 4 }}>
                  {selectedRoute.sigunguName && <span style={{ marginLeft: 8, color: '#888' }}>{selectedRoute.sigunguName}_버스_</span>}
                  <span style={{ fontSize: 20, color: '#3498db' }}>
                    {selectedRoute.routeno}
                  </span>
                  {<span style={{ marginLeft: 8 }}>[{selectedRoute.startnodenm || "?"}~{selectedRoute.endnodenm || "?"}]</span>}
                  {/*<span style={{ marginLeft: 8, fontSize: 13, color: '#aaa' }}> {selectedRoute.routeId}</span>*/}
                </div>
              ) : (() => {
                // routeId 문자열에서 정보 추출 (기존 포맷 유지)
                const match = routeId.match(/^(\w+)(?: \(([^)]*)\))? \[([^~]*)~([^\]]*)\]/);
                if (match) {
                  const [, routeIdVal, sigunguName, startnodenm, endnodenm] = match;
                  let routeno = null;
                  if (routeList && routeList.length === 1 && routeList[0].routeId === routeIdVal) routeno = routeList[0].routeno;
                  return (
                    <>
                      <span style={{ fontSize: 18, color: '#3498db' }}>
                        {routeno ? `노선번호: ${routeno}` : `undifind????`}
                      </span>
                      {sigunguName && <span style={{ marginLeft: 8, color: '#888' }}>({sigunguName})</span>}
                      {<span style={{ marginLeft: 8 }}>[{startnodenm}~{endnodenm}]</span>}
                      <span style={{ marginLeft: 8, fontSize: 13, color: '#aaa' }}>{routeIdVal}</span>
                    </>
                  );
                }
                return <span>{routeId}</span>;
              })()}
              
              {/* 배차간격 정보 표시 */}
              {routeInterval && (
                <div style={{ marginTop: 8, fontSize: 14, color: '#666', background: '#f8f9fa', padding: 8, borderRadius: 4 }}>
                  <div style={{ fontWeight: 'bold', marginBottom: 4 }}>배차간격(분) - 

                    <span> 평일: {routeInterval.weekday}</span>
                    <span> 토일: {routeInterval.saturday}</span>
                    <span> 공휴일: {routeInterval.holiday}</span>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* 버스 정차 정류소 목록 */}
          {lastRouteInfo && lastRouteInfo.routeId && (
            <>
              {/* 정류장 검색 입력창 */}
              <div style={{ marginBottom: 8 }}>
                <input
                  type="text"
                  value={stopSearch}
                  onChange={e => setStopSearch(e.target.value)}
                  placeholder="정류장명 검색(종점>기점 순)"
                  style={{ width: '100%', fontSize: 15, padding: 6, borderRadius: 6, border: '1px solid #ddd' }}
                />
                {/* 승하차지 정보 표시 */}
                {(selectedStops.length > 0) && (() => {
                  let boarding = null, alighting = null;
                  if (selectedStops.length === 2) {
                    const [a, b] = selectedStops;
                    if (Number(a.nodeord) <= Number(b.nodeord)) {
                      boarding = a;
                      alighting = b;
                    } else {
                      boarding = b;
                      alighting = a;
                    }
                  }
                  return (
                    <div style={{ marginTop: 6, marginBottom: 2, fontSize: 15, color: '#3498db', fontWeight: 'bold' }}>
                      {selectedStops.length === 1 && <>승차지: {selectedStops[0].nodenm || selectedStops[0].stopName} {'>>'} (미지정)</>}
                      {selectedStops.length === 2 && <>
                        승차지: {(boarding?.nodenm || boarding?.stopName) ?? '(미지정)'} {'>>'} 하차지: {(alighting?.nodenm || alighting?.stopName) ?? '(미지정)'}
                      </>}
                    </div>
                  );
                })()}
              </div>
              <div style={{
                marginTop: 10,
                maxHeight: 260,
                overflowY: "auto",
                background: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: 6,
                padding: 10
              }}>
                {busStopsLoading && <div>정류소 목록 불러오는 중...</div>}
                {busStopsError && <div style={{ color: "red" }}>{busStopsError}</div>}
                {busStops && busStops.length > 0 && (
                  <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {busStops
                      .filter(stop => stop.nodenm && stop.nodenm.includes(stopSearch))
                      .map((stop, idx) => (
                        <li
                          key={stop.nodeid + '-' + stop.nodeord}
                          onClick={() => handleStopClick(stop)}
                          style={{
                            padding: "6px 0",
                            fontWeight: stop.nodenm === lastRouteInfo.endnodenm ? "bold" : "normal",
                            color: stop.nodenm === lastRouteInfo.endnodenm ? "#e74c3c" : "#222",
                            borderBottom: "1px solid #f0f0f0",
                            cursor: "pointer",
                            backgroundColor: isStopSelected(stop) ? "#e8f4fd" : "transparent",
                            borderRadius: 4,
                          }}
                        >
                          {idx + 1}. {stop.nodenm}
                          {stop.nodenm === lastRouteInfo.endnodenm && " (회차지)"}
                          {getStopType(stop) && (
                            <span style={{
                              marginLeft: 8,
                              fontSize: 12,
                              color: getStopType(stop) === "승차지" ? "#27ae60" : "#e67e22",
                              fontWeight: "bold"
                            }}>
                              [{getStopType(stop)}]
                            </span>
                          )}
                        </li>
                      ))}
                  </ul>
                )}

              </div>
            </>
          )}
          
          {/* 정류소 목록 하단 버튼들 */}
          {lastRouteInfo && lastRouteInfo.routeId && busStops && busStops.length > 0 && (
            <div style={{
              marginTop: 12,
              display: "flex",
              justifyContent: "space-between",
              gap: 10
            }}>
              {/* 서울(11) 또는 경기(31XXX) 지역에서만 지도에서 보기 버튼 표시 
              {(String(selectedRoute?.cityCode).startsWith("11") || String(selectedRoute?.cityCode).startsWith("31")) && (
                <button
                  onClick={() => {
                    onShowOnMap(routeViewData);
                  }}
                  style={{
                    flex: 1,
                    padding: "8px 12px",
                    fontSize: 14,
                    background: "#3498db",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer"
                  }}
                >
                  지도에서 보기
                </button>
              )}
              */}

              <button
                onClick={() => {
                  // 승차지/하차지 정보 추출
                  if (selectedStops.length === 2) {
                    const [first, second] = selectedStops;
                    const boarding = first.nodeord <= second.nodeord ? first : second;
                    const alighting = first.nodeord > second.nodeord ? first : second;
                    
                    const info = {
                      routeId: selectedRoute?.routeId,
                      routeName: selectedRoute?.routeno,
                      cityCode: selectedRoute?.cityCode,

                      routeType: formatRouteType(selectedRoute?.routeType), // 추가: 변환 적용
                      label: "배차: "+routeInterval.weekday +"분 "+routeInterval.saturday +"분 "+ routeInterval.holiday+"분",
                      boarding: {
                        name: boarding.nodenm || boarding.stopName,
                        lat: boarding.gpslati || boarding.lat,
                        lng: boarding.gpslong || boarding.lng
                      },
                      alighting: {
                        name: alighting.nodenm || alighting.stopName,
                        lat: alighting.gpslati || alighting.lat,
                        lng: alighting.gpslong || alighting.lng
                      }
                    };
                    if (typeof onCompleteSelection === 'function') {
                      onCompleteSelection(info);
                    }
                  } else {
                    alert("승차지와 하차지를 모두 선택해주세요.");
                  }
                }}
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  fontSize: 14,
                  background: selectedStops.length === 2 ? "#27ae60" : "#95a5a6",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  cursor: selectedStops.length === 2 ? "pointer" : "not-allowed"
                }}
                disabled={selectedStops.length !== 2}
              >
                선택 완료
              </button>
            </div>
          )}
          {routeError && (
            <div style={{ color: "red" }}>
              {routeError}
            </div>
          )}
        </div>
      )}
    </div>
  );

}

export default SearchTab;



